import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Loader2, Plus, Pencil, Building2, Warehouse, MoreVertical, Power, PowerOff, Trash2 } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const EMPTY = { codigo: "", nome: "", tipo: "UNIDADE", cidade: "", logo_path: null, ativo: true, is_fabrica: false };
const FILTROS = [{ v: "ATIVAS", l: "Ativas" }, { v: "INATIVAS", l: "Inativas" }, { v: "TODAS", l: "Todas" }];

export default function Unidades() {
  const { user } = useAuth();
  const isAdmin = ["SUPER_ADMIN", "MASTER"].includes(user.role);
  const [units, setUnits] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filtro, setFiltro] = useState("ATIVAS");
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => api.get("/units").then((r) => setUnits(r.data));
  useEffect(() => { load(); }, []);

  const open = (u) => { if (u) { setForm(u); setEditId(u.id); } else { setForm(EMPTY); setEditId(null); } setModal(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.codigo || !form.nome) return toast.error("Código e nome obrigatórios");
    setSaving(true);
    try {
      const payload = { codigo: form.codigo, nome: form.nome, tipo: form.tipo, cidade: form.cidade, logo_path: form.logo_path || null, ativo: form.ativo, is_fabrica: !!form.is_fabrica };
      if (editId) await api.put(`/units/${editId}`, payload);
      else await api.post("/units", payload);
      toast.success("Unidade salva");
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };

  const toggleAtivo = async (u) => {
    try {
      await api.put(`/units/${u.id}`, { codigo: u.codigo, nome: u.nome, tipo: u.tipo, cidade: u.cidade || "", logo_path: u.logo_path || null, ativo: u.ativo === false, is_fabrica: !!u.is_fabrica });
      toast.success(u.ativo === false ? "Unidade reativada" : "Unidade desativada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/units/${delTarget.id}`);
      toast.success("Unidade excluída");
      setDelTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Não foi possível excluir"); }
    finally { setDeleting(false); }
  };

  const visiveis = useMemo(() => {
    if (!units) return null;
    return units.filter((u) => filtro === "TODAS" ? true : filtro === "ATIVAS" ? u.ativo !== false : u.ativo === false);
  }, [units, filtro]);

  return (
    <div className="space-y-5" data-testid="unidades-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Cadastros</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase">Unidades</h1>
        </div>
        <Button onClick={() => open(null)} data-testid="btn-nova-unidade"><Plus className="w-4 h-4" /> Nova Unidade</Button>
      </div>

      <div className="flex items-center gap-2">
        {FILTROS.map((f) => (
          <Button key={f.v} variant={filtro === f.v ? "default" : "outline"} size="sm" onClick={() => setFiltro(f.v)} data-testid={`filtro-un-${f.v}`}>{f.l}</Button>
        ))}
      </div>

      {!visiveis ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : !visiveis.length ? (
        <Card className="p-10 text-center text-muted-foreground">Nenhuma unidade neste filtro.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {visiveis.map((u) => {
            const inativa = u.ativo === false;
            return (
              <Card key={u.id} className={`bg-card/70 p-4 flex items-start gap-3 ${inativa ? "opacity-70" : ""}`} data-testid={`unidade-${u.codigo}`}>
                <div className={`w-11 h-11 rounded-md flex items-center justify-center shrink-0 ${u.tipo === "CD" ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>
                  {u.tipo === "CD" ? <Warehouse className="w-6 h-6" /> : <Building2 className="w-6 h-6" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-bold">{u.codigo}</span>
                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-sm ${u.tipo === "CD" ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>{u.tipo}</span>
                    {u.is_fabrica && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-300" data-testid={`badge-fabrica-${u.codigo}`}>Fábrica</span>}
                    {inativa && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-red-500/15 text-red-300 border border-red-500/40 font-mono" data-testid={`badge-inativa-${u.codigo}`}>Inativa</span>}
                  </div>
                  <div className="text-sm font-medium truncate">{u.nome}</div>
                  <div className="text-xs text-muted-foreground">{u.cidade}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => open(u)} data-testid={`edit-un-${u.codigo}`}><Pencil className="w-4 h-4" /></Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`menu-un-${u.codigo}`}><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => toggleAtivo(u)} data-testid={`toggle-un-${u.codigo}`}>
                        {inativa ? <><Power className="w-4 h-4" /> Ativar</> : <><PowerOff className="w-4 h-4" /> Desativar</>}
                      </DropdownMenuItem>
                      {isAdmin && u.tipo !== "CD" && (<>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-400" onClick={() => setDelTarget(u)} data-testid={`del-un-${u.codigo}`}><Trash2 className="w-4 h-4" /> Excluir</DropdownMenuItem>
                      </>)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Criar / Editar */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Editar" : "Nova"} Unidade</DialogTitle>
            <DialogDescription className="sr-only">Formulário de cadastro de unidade (código, tipo, nome e cidade).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Código</Label><Input value={form.codigo} onChange={(e) => set("codigo", e.target.value)} data-testid="u-codigo" /></div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
                <SelectTrigger data-testid="u-tipo"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="CD">Centro de Distribuição</SelectItem><SelectItem value="UNIDADE">Unidade</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Nome</Label><Input value={form.nome} onChange={(e) => set("nome", e.target.value)} data-testid="u-nome" /></div>
            <div className="col-span-2"><Label>Cidade</Label><Input value={form.cidade} onChange={(e) => set("cidade", e.target.value)} data-testid="u-cidade" /></div>
            <label className="col-span-2 flex items-center justify-between rounded-md border border-border p-3 cursor-pointer">
              <span><span className="text-sm font-semibold">Unidade fábrica</span><span className="block text-xs text-muted-foreground">Habilita o módulo Produção (almoxarifado, produção e acabados)</span></span>
              <Switch checked={!!form.is_fabrica} onCheckedChange={(v) => set("is_fabrica", v)} data-testid="u-is-fabrica" />
            </label>
            <div className="col-span-2"><ImageUpload label="Logo / foto da unidade" value={form.logo_path} onChange={(p) => set("logo_path", p)} testid="unidade-logo" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-salvar-unidade">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmar exclusão */}
      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) setDelTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="del-un-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-red-300">Excluir Unidade</DialogTitle>
            <DialogDescription>
              Você está prestes a excluir <b className="text-foreground">{delTarget?.codigo} — {delTarget?.nome}</b>. Esta ação é permanente e não pode ser desfeita.
              Se a unidade tiver pedidos, movimentações ou recebimentos vinculados, a exclusão será bloqueada — nesse caso, use <b>Desativar</b>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting} data-testid="btn-confirm-del-un">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4" /> Excluir definitivamente</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
